/**
 * components/notifyTarget.js
 * МЭДЭГДЛИЙН ТӨРӨЛ ба «АЖИЛ РУУ ШИЛЖИХ» ЦОР ГАНЦ дүрэм.
 *
 * Хэрэглэгч (2026-10-28): «бүртгэлийн хуудасны notification хэсгийг хийе,
 * хэнд ямар даалгавар ирсэн — гүйцэтгэл юм уу, хувиарлалт юм уу,
 * гүйцэтгэх ажил юм уу гэх мэт бүгд ялгаатай мэдээлэл ирнэ, мөн энэ
 * мэдээллээс дамжиж тэр хийх ажил руугаа орох гүйцэтгэх боломжтой байна».
 *
 * ━━ ЯАГААД ТУСДАА ФАЙЛ ВЭ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * Шилжих дүрэм нь өмнө нь `NotificationBell.js` дотор ШИНГЭСЭН байв.
 * Бүртгэлийн хуудсан дээр ижил зүйл хэрэгтэй болсон тул хуулбарлавал
 * ХОЁР ХУВИЛБАР болж, нэгийг зассан нь нөгөөд тусахгүй — шинэ төрөл
 * нэмэхэд нэг газраас л «алга» болно. Иймд ЭНД нэг л удаа.
 *
 * ━━ ТӨРЛҮҮД (баазаас хэмжсэн, 2026-10-28) ━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 *   entity_type                тоо   утга
 *   hazard                    1337   шинэ аюул (мэдээлэл)
 *   inspection_assignment      341   ХУВИАРЛАЛТ — «танд даалгавар оноов»
 *   inspection_fix_submitted    92   ГҮЙЦЭТГЭЛ — «биелэлт ирлээ, батла»
 *   inspection_fix_verified     16   баталгаажлаа / буцаалаа
 *
 * ⚠ `payload` нь ЗӨВХӨН `inspection_fix_submitted`-д бий
 *   ({inspection_id, item_id}). Бусдад `null` тул `entity_id` ба
 *   `entity_ref`-ээс л шилжинэ:
 *     hazard                → entity_id = hazard_reports.id
 *     inspection_assignment → entity_id = inspection_item_result.id,
 *                             entity_ref = шалгалтын код. Хариуцагч нь
 *                             ажлаа «Миний даалгавар» дээр гүйцэтгэдэг
 *                             тул тийш шилжүүлнэ (мөрийн id хэрэггүй).
 */

/** Мэдэгдлийн СЕМАНТИК төрөл — UI нь үүгээр ялгаж харуулна. */
export const NOTIFY_KINDS = {
  assignment: {
    key: "assignment",
    label: "Хувиарлалт",
    hint: "Танд оногдсон даалгавар",
    color: "#B45309",
    icon: "fa-user-tag",
    action: "Гүйцэтгэх",
  },
  fix_submitted: {
    key: "fix_submitted",
    label: "Гүйцэтгэл",
    hint: "Биелэлт ирлээ — баталгаажуулна уу",
    color: "#0F6E56",
    icon: "fa-clipboard-check",
    action: "Баталгаажуулах",
  },
  fix_verified: {
    key: "fix_verified",
    label: "Шийдвэр",
    hint: "Баталгаажсан эсвэл буцаагдсан",
    color: "#1D4ED8",
    icon: "fa-circle-check",
    action: "Харах",
  },
  hazard: {
    key: "hazard",
    label: "Аюул",
    hint: "Шинээр бүртгэгдсэн аюул",
    color: "#CF1322",
    icon: "fa-triangle-exclamation",
    action: "Нээх",
  },
  other: {
    key: "other",
    label: "Бусад",
    hint: "",
    color: "#8A94A6",
    icon: "fa-bell",
    action: "Нээх",
  },
};

/** ДААЛГАВРЫН төрлүүд — «хийх ажил» гэж тооцогдох entity_type-ууд.
 *
 * ⚠ `hazard` ЭНД ОРОХГҮЙ: шинэ аюул нь танд оногдсон АЖИЛ биш,
 *   МЭДЭЭЛЭЛ. Мөн тоо хэмжээгээр нь (1337 мөр) бусдыг нураадаг —
 *   хэмжсэн (ТН 43952): 390 уншаагүйн эхний 50 нь бүгд hazard байсан
 *   тул даалгавар цонхноос нурж байв. Аюул нь толгойн хонхон дор
 *   хэвээр.
 * ⚠ Серверийн `?entity_type=` шүүлтэд ЭНЭ жагсаалт шууд явна. */
export const TASK_ENTITY_TYPES = [
  "inspection_assignment",
  "inspection_fix_submitted",
  "inspection_fix_verified",
];

const BY_ENTITY = {
  inspection_assignment: "assignment",
  inspection_fix_submitted: "fix_submitted",
  inspection_fix_verified: "fix_verified",
  hazard: "hazard",
};

/** Мэдэгдлийн төрлийн тодорхойлолт (танигдаагүй бол `other`). */
export function notifyKind(item) {
  const k = BY_ENTITY[String(item?.entity_type || "")] || "other";
  return NOTIFY_KINDS[k];
}

/**
 * «Ажил руу шилжих» зам. Шилжих боломжгүй бол `null`.
 *
 * ⚠ `inspection_fix_submitted` дээр `state.from` ЗААВАЛ: буцахад
 *   мэдэгдэл дарсан хуудсандаа эргэж ирнэ. Эс бөгөөс шалгагч
 *   /inspection/xab дээр гарч, ажлаа алддаг (NotificationBell-ийн
 *   анхны тайлбар).
 */
export function notifyRoute(item) {
  const t = String(item?.entity_type || "");
  if (t === "hazard" && item?.entity_id) {
    return { to: `/hazard/${item.entity_id}` };
  }
  if (t === "inspection_assignment" || t === "inspection_fix_verified") {
    // Хариуцагч ажлаа «Миний даалгавар» дээр гүйцэтгэнэ
    return { to: "/my-assignments" };
  }
  if (t === "inspection_fix_submitted") {
    const iid = item?.payload?.inspection_id;
    return {
      to: iid ? `/inspection/xab/${iid}` : "/inspection/register",
      state: { from: window.location.pathname },
    };
  }
  return null;
}

export default notifyRoute;
